import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { 
  ShoppingBag, Scissors, Star, MapPin, Clock, MessageCircle, 
  Instagram, ArrowRight, Menu, X, Check, ChevronRight
} from 'lucide-react';

// === Main Component (Multi-Page Router) ===
const MidBoutiqueLanding = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (page: string) => {
    setCurrentPage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageVariants: Variants = {
    initial: { opacity: 0, y: 15 },
    in: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
    out: { opacity: 0, y: -15, transition: { duration: 0.3, ease: "easeIn" as const } }
  };

  return (
    <div className="bg-[#FCFCFA] text-stone-800 min-h-screen font-sans selection:bg-rose-900 selection:text-white flex flex-col overflow-x-hidden">
      
      {/* === ELEGANT NAVBAR === */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-md border-b border-stone-200 py-4 shadow-sm' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between items-center">
          
          <div onClick={() => handleNavClick('home')} className="flex flex-col items-center cursor-pointer group">
            <span className="font-serif text-3xl tracking-[0.15em] text-stone-900 uppercase leading-none group-hover:text-rose-900 transition-colors">Elegance</span>
            <span className="text-[9px] tracking-[0.4em] text-stone-500 uppercase mt-1">House of Fashion</span>
          </div>
          
          <div className="hidden md:flex items-center gap-10 text-xs font-bold tracking-[0.15em] uppercase text-stone-500">
            {['home', 'collections', 'bespoke', 'contact'].map((item) => (
              <button 
                key={item} 
                onClick={() => handleNavClick(item)}
                className={`transition-colors hover:text-rose-900 ${currentPage === item ? 'text-rose-900 border-b border-rose-900 pb-1' : ''}`}
              >
                {item === 'home' ? 'Discover' : item === 'bespoke' ? 'Tailoring' : item}
              </button>
            ))}
          </div>

          <button onClick={() => handleNavClick('contact')} className="hidden md:block border border-stone-900 text-stone-900 px-6 py-2.5 text-[10px] font-bold tracking-widest uppercase hover:bg-stone-900 hover:text-white transition-colors">
            Book Appointment
          </button>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-stone-900">
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: '100vh' }} exit={{ opacity: 0, height: 0 }} className="absolute top-full left-0 w-full bg-white flex flex-col items-center justify-center gap-8 border-b border-stone-200">
               {['home', 'collections', 'bespoke', 'contact'].map((item) => (
                 <button key={item} onClick={() => handleNavClick(item)} className="text-2xl font-serif text-stone-900 hover:text-rose-900 uppercase tracking-widest">
                   {item === 'home' ? 'Discover' : item}
                 </button>
               ))}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* === PAGE ROUTING AREA === */}
      <main className="flex-grow pt-24">
        <AnimatePresence mode="wait">
          <motion.div key={currentPage} initial="initial" animate="in" exit="out" variants={pageVariants}>
            {currentPage === 'home' && <HomeView setPage={handleNavClick} />}
            {currentPage === 'collections' && <CollectionsView />}
            {currentPage === 'bespoke' && <BespokeView setPage={handleNavClick} />}
            {currentPage === 'contact' && <ContactView />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* === PREMIUM FOOTER === */}
      <footer className="bg-stone-950 text-stone-300 pt-20 pb-10 mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-2">
            <span className="font-serif text-3xl tracking-[0.15em] uppercase text-white mb-6 block">Elegance</span>
            <p className="font-light text-sm leading-relaxed max-w-sm mb-8 text-stone-400">
              Where tradition meets contemporary design. We craft garments that celebrate your individuality, using only the finest fabrics and masterful embroidery.
            </p>
            <div className="flex gap-4">
              <a href="#" className="border border-stone-700 p-2 hover:bg-white hover:text-stone-950 transition-colors"><Instagram size={18} /></a>
            </div>
          </div>
          <div>
            <h4 className="text-[10px] font-bold tracking-[0.2em] uppercase mb-6 text-stone-500">Navigation</h4>
            <ul className="space-y-3 text-sm font-light">
              <li className="hover:text-white cursor-pointer" onClick={() => handleNavClick('home')}>Discover Elegance</li>
              <li className="hover:text-white cursor-pointer" onClick={() => handleNavClick('collections')}>Our Lookbook</li>
              <li className="hover:text-white cursor-pointer" onClick={() => handleNavClick('bespoke')}>Custom Tailoring</li>
              <li className="hover:text-white cursor-pointer" onClick={() => handleNavClick('contact')}>Visit The Store</li>
            </ul>
          </div>
          <div>
            <h4 className="text-[10px] font-bold tracking-[0.2em] uppercase mb-6 text-stone-500">Contact</h4>
            <ul className="space-y-3 text-sm font-light">
              <li>12, Fashion Street Market<br/>City Center, 201001</li>
              <li className="text-white mt-4">+91 98765 43210</li>
              <li>info@eleganceboutique.demo</li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center border-t border-stone-800 pt-8 text-[10px] tracking-widest uppercase text-stone-600">
          © {new Date().getFullYear()} Elegance House of Fashion. Designed for Demos.
        </div>
      </footer>
    </div>
  );
};

// ==========================================
// 1. HOME VIEW
// ==========================================
const HomeView = ({ setPage }: { setPage: (page: string) => void }) => (
  <div>
    {/* Editorial Split Hero */}
    <section className="relative min-h-[85vh] flex items-center px-6 lg:px-12 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
        <div className="order-2 lg:order-1 z-10">
          <p className="text-rose-900 text-[10px] font-bold tracking-[0.3em] uppercase mb-6 border-l-2 border-rose-900 pl-4">Autumn / Winter Collection</p>
          <h1 className="text-5xl md:text-7xl font-serif text-stone-900 mb-8 leading-[1.1]">
            Define Your <br/><span className="italic text-stone-400">Signature</span> Style.
          </h1>
          <p className="text-stone-500 font-light text-lg mb-10 max-w-md leading-relaxed">
            From meticulously crafted bridal wear to sharp everyday silhouettes. Experience fashion that speaks before you do.
          </p>
          <div className="flex items-center gap-6">
            <button onClick={() => setPage('collections')} className="bg-stone-900 text-white px-8 py-4 text-xs font-bold tracking-widest uppercase hover:bg-rose-900 transition-colors flex items-center gap-2">
              View Lookbook <ArrowRight size={16} />
            </button>
          </div>
        </div>
        <div className="order-1 lg:order-2 relative h-[500px] lg:h-[700px] w-full">
          <div className="absolute top-10 -left-10 w-full h-full bg-stone-100 -z-10"></div>
          <img src="https://images.pexels.com/photos/1055691/pexels-photo-1055691.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Fashion Model" className="w-full h-full object-cover shadow-2xl" />
        </div>
      </div>
    </section>

    {/* Featured Categories (Preview) */}
    <section className="py-32 px-6 lg:px-12 bg-white mt-20">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16">
          <div>
            <h2 className="text-4xl font-serif text-stone-900 mb-4">Season's <span className="italic text-stone-400">Highlights</span></h2>
            <div className="w-16 h-px bg-stone-300"></div>
          </div>
          <button onClick={() => setPage('collections')} className="text-stone-500 hover:text-stone-900 text-[10px] tracking-[0.2em] uppercase font-bold mt-6 md:mt-0 flex items-center gap-1">
            See All <ChevronRight size={14}/>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Bridal Couture", img: "https://tse1.mm.bing.net/th/id/OIP.TYYMekAlH6-D4FQBc94_FQHaI4?pid=Api&P=0&h=180" },
            { title: "Ready To Wear", img: "https://wwd.com/wp-content/uploads/2025/03/chanel-fall-25-r-dom-pfw-0015.jpg?resize=100" },
            { title: "Festive Drapes", img: "https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg?auto=compress&cs=tinysrgb&w=800" }
          ].map((item, i) => (
            <div key={i} className="group cursor-pointer" onClick={() => setPage('collections')}>
              <div className="overflow-hidden mb-6 h-[450px]">
                <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
              </div>
              <h3 className="font-serif text-2xl text-stone-900">{item.title}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* The Atelier (About Snippet) */}
    <section className="py-32 px-6 lg:px-12 bg-stone-900 text-white text-center">
      <div className="max-w-3xl mx-auto">
        <Scissors size={40} className="mx-auto mb-8 text-stone-500" />
        <h2 className="text-4xl md:text-5xl font-serif mb-8 leading-tight">Mastery in every thread.</h2>
        <p className="font-light text-lg text-stone-400 mb-10 leading-relaxed">
          Beyond our ready-to-wear collections, Elegance houses a premium tailoring atelier. From bespoke bridal fits to altering your favorite vintage pieces, our master tailors ensure perfection.
        </p>
        <button onClick={() => setPage('bespoke')} className="border border-white text-white px-8 py-4 text-xs font-bold tracking-widest uppercase hover:bg-white hover:text-stone-900 transition-colors">
          Explore Tailoring Services
        </button>
      </div>
    </section>
  </div>
);

// ==========================================
// 2. COLLECTIONS VIEW (Lookbook)
// ==========================================
const CollectionsView = () => (
  <div className="py-12 px-6 lg:px-12 max-w-7xl mx-auto">
    <div className="text-center mb-20">
      <h1 className="text-5xl md:text-6xl font-serif text-stone-900 mb-6">The <span className="italic text-stone-400">Lookbook</span></h1>
      <p className="text-stone-500 max-w-xl mx-auto">Browse our latest curations. From heavy embroidery to minimalist chic, find your perfect ensemble.</p>
    </div>

    {/* Grid of 6 Items */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-16 gap-x-8">
      {[
        { title: "The Royal Bride", price: "Starts at ₹45,000", img: "https://weddingplz-res.cloudinary.com/image/upload/f_auto,fl_lossy,q_auto/v1/live/photo-gallery/real_wedding/139/134a-anuradha-and-manan--2.JPG" },
        { title: "Velvet Elegance", price: "Ready to wear", img: "https://5.imimg.com/data5/SELLER/Default/2024/9/447891290/MM/EI/XL/62853961/ready-to-wear-black-embroidered-velvet-lehenga-1000x1000.jpg" },
        { title: "Summer Florals", price: "Festive Collection", img: "https://i.pinimg.com/originals/e9/38/21/e93821836afd8e3268e72e12cf1c9e48.jpg" },
        { title: "Classic Kurta Sets", price: "Everyday Luxury", img: "https://images.pexels.com/photos/1689731/pexels-photo-1689731.jpeg?auto=compress&cs=tinysrgb&w=800" },
        { title: "Reception Gowns", price: "Custom Made", img: "https://www.dhresource.com/webp/m/2023-luxury-sequined-evening-dresses-beaded/f3-albu-km-s-30-f13f9581-560b-4e65-a988-ba2b626f84fc.jpg" },
        { title: "Men's Sherwani", price: "Starts at ₹15,000", img: "https://images.pexels.com/photos/3755918/pexels-photo-3755918.jpeg?auto=compress&cs=tinysrgb&w=800" }
      ].map((item, i) => (
        <div key={i} className="group">
          <div className="overflow-hidden mb-6 aspect-[3/4] bg-stone-100">
            <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          </div>
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-serif text-xl text-stone-900 mb-1">{item.title}</h3>
              <p className="text-stone-500 text-xs tracking-wider uppercase">{item.price}</p>
            </div>
            <ShoppingBag size={20} className="text-stone-400 group-hover:text-stone-900 transition-colors cursor-pointer" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ==========================================
// 3. BESPOKE TAILORING VIEW (Services)
// ==========================================
const BespokeView = ({ setPage }: { setPage: (page: string) => void }) => (
  <div className="pb-20">
    <div className="bg-stone-100 py-20 px-6 mb-20">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="text-5xl md:text-6xl font-serif text-stone-900 mb-6">Bespoke <span className="italic text-stone-400">Tailoring</span></h1>
        <p className="text-stone-600 text-lg">Your body is unique. Your clothes should be too. Experience the luxury of garments crafted specifically to your measurements.</p>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
        <div className="space-y-8">
          <h2 className="text-3xl font-serif text-stone-900">The Process</h2>
          <div className="space-y-8">
            {[
              { step: "01. Consultation", desc: "Meet our head designer to discuss fabric, cut, and style. Bring your references or choose from our catalogs." },
              { step: "02. Measurement", desc: "Over 20 precise body measurements are taken to create a unique paper pattern just for you." },
              { step: "03. The Fitting", desc: "A trial session (basting) ensures the silhouette is perfect before the final seams are stitched." }
            ].map((s, i) => (
              <div key={i} className="flex gap-4 items-start">
                <Check size={20} className="text-rose-900 shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-stone-900 mb-2 uppercase tracking-widest text-xs">{s.step}</h4>
                  <p className="text-stone-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setPage('contact')} className="bg-stone-900 text-white px-8 py-4 text-xs font-bold tracking-widest uppercase hover:bg-rose-900 transition-colors mt-4">
            Book a Consultation
          </button>
        </div>
        <div>
          <img src="https://images.pexels.com/photos/461035/pexels-photo-461035.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Tailoring" className="w-full h-[600px] object-cover shadow-lg" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="p-10 border border-stone-200">
          <Star size={30} className="text-stone-400 mb-6" />
          <h3 className="font-serif text-2xl text-stone-900 mb-4">Bridal Trousseau</h3>
          <p className="text-stone-500 mb-6 font-light">Comprehensive packages for the bride and family. From Haldi outfits to the grand reception gown.</p>
          <ul className="space-y-2 text-sm text-stone-600 font-medium mb-6">
            <li>• Hand Embroidery (Zardosi, Aari)</li>
            <li>• Dyeing & Fabric Sourcing</li>
            <li>• Urgent 7-Day Delivery Available</li>
          </ul>
        </div>
        <div className="p-10 border border-stone-200">
          <Scissors size={30} className="text-stone-400 mb-6" />
          <h3 className="font-serif text-2xl text-stone-900 mb-4">Alterations & Fixes</h3>
          <p className="text-stone-500 mb-6 font-light">Bought something online that doesn't fit right? We expertly alter readymade garments to look custom-made.</p>
          <ul className="space-y-2 text-sm text-stone-600 font-medium mb-6">
            <li>• Lehenga resizing</li>
            <li>• Gown shortening & fitting</li>
            <li>• Same-day alterations (Walk-in)</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

// ==========================================
// 4. CONTACT VIEW (Visit Us)
// ==========================================
const ContactView = () => (
  <div className="py-12 px-6 lg:px-12 max-w-7xl mx-auto">
    <div className="text-center mb-20">
      <h1 className="text-5xl md:text-6xl font-serif text-stone-900 mb-6">Visit <span className="italic text-stone-400">The Store</span></h1>
      <p className="text-stone-500 max-w-xl mx-auto">Drop by to explore fabrics, try on our collections, or consult with our tailors.</p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
      
      {/* Contact Info */}
      <div className="bg-stone-50 p-10 lg:p-16 border border-stone-100">
        <h3 className="font-serif text-3xl text-stone-900 mb-8">Store Details</h3>
        
        <div className="space-y-8">
          <div className="flex items-start gap-4">
            <MapPin size={24} className="text-rose-900 shrink-0" />
            <div>
              <h4 className="font-bold text-stone-900 uppercase tracking-widest text-xs mb-2">Location</h4>
              <p className="text-stone-600">Shop No. 12, Fashion Street Market,<br/>Main Road, City Center, 201001</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <Clock size={24} className="text-rose-900 shrink-0" />
            <div>
              <h4 className="font-bold text-stone-900 uppercase tracking-widest text-xs mb-2">Hours</h4>
              <p className="text-stone-600">Mon - Sat: 10:30 AM - 9:00 PM<br/>Sunday: Closed</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <MessageCircle size={24} className="text-rose-900 shrink-0" />
            <div>
              <h4 className="font-bold text-stone-900 uppercase tracking-widest text-xs mb-2">Direct Message</h4>
              <p className="text-stone-600 mb-4">Fastest way to get a quote or book an appointment.</p>
              <a href="https://wa.me/910000000000" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-2 rounded-full text-xs font-bold tracking-widest uppercase hover:bg-[#20bd5a] transition-colors">
                 WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Form */}
      <div className="p-10 lg:p-16 border border-stone-200">
        <h3 className="font-serif text-3xl text-stone-900 mb-2">Book an Appointment</h3>
        <p className="text-stone-500 mb-8 text-sm">Skip the wait. Schedule a time with our head designer.</p>
        
        <form className="space-y-6" onSubmit={(e) => { 
  e.preventDefault(); 
  alert('Demo: Application Sent.'); 
  e.currentTarget.reset(); // Yeh automatically form ke saare inputs ko clear kar dega
}}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input type="text" required placeholder="First Name" className="w-full border-b border-stone-300 py-3 focus:outline-none focus:border-rose-900 bg-transparent placeholder-stone-400" />
            <input type="text" required placeholder="Last Name" className="w-full border-b border-stone-300 py-3 focus:outline-none focus:border-rose-900 bg-transparent placeholder-stone-400" />
          </div>
          <input type="tel" required placeholder="Phone Number" className="w-full border-b border-stone-300 py-3 focus:outline-none focus:border-rose-900 bg-transparent placeholder-stone-400" />
          <select className="w-full border-b border-stone-300 py-3 focus:outline-none focus:border-rose-900 bg-transparent text-stone-600">
            <option>Bridal Consultation</option>
            <option>Custom Stitching Measurement</option>
            <option>Alteration Drop-off</option>
          </select>
          <input type="date" required className="w-full border-b border-stone-300 py-3 focus:outline-none focus:border-rose-900 bg-transparent text-stone-600" />
          
          <button type="submit" className="w-full bg-stone-900 text-white px-8 py-4 text-xs font-bold tracking-widest uppercase hover:bg-rose-900 transition-colors mt-4">
            Confirm Request
          </button>
        </form>
      </div>

    </div>
  </div>
);

export default MidBoutiqueLanding;